import { apiClient } from './client';
import type { 
  CelebrationVenueSearch,
  CreateCelebrationVenueSearchDto,
  Venue, 
  VenueWithGoogleDetails, 
  VenueSearchRequest,
  VenueSearchResult,
  GooglePlaceSearchResult
} from '@/types/venue.types';

class VenueService {

  /**
   * Get all venues (minimal data from our database)
   */
  async getVenues(): Promise<Venue[]> {
    const response = await apiClient.get('/venues');
    return response.data.data;
  }

  async getCelebrationVenues(celebrationId: number): Promise<Venue[]> {
    const response = await apiClient.get(`/celebrations/${celebrationId}/venues`);
    return response.data.data;
  }

  /**
   * Get a specific venue
   */
  async getVenue(id: number): Promise<Venue> {
    const response = await apiClient.get(`/venues/${id}`);
    return response.data.data;
  }

  /**
   * Get venue with full Google Places details
   */
  async getVenueWithDetails(id: number): Promise<VenueWithGoogleDetails> {
    const response = await apiClient.get(`/venues/${id}/details`);
    return response.data.data;
  }

  async getVenueByPlaceId(placeId: string): Promise<GooglePlaceSearchResult & { website?: string }> {
    const response = await apiClient.get(`/venues/places/${placeId}`);
    return response.data.data;
  }

  /**
   * Delete venue
   */
  async deleteVenue(id: number): Promise<void> {
    await apiClient.delete(`/venues/${id}`);
  }

  /**
   * Search venues (combines saved venues + Google Places)
   */
  async searchVenues(request: VenueSearchRequest): Promise<VenueSearchResult> {
    const response = await apiClient.post('/venues/search', request);
    return response.data.data;
  }

  async getCelebrationVenueSearches(celebrationId: number): Promise<CelebrationVenueSearch[]> {
    const response = await apiClient.get(`/celebrations/${celebrationId}/venue-searches`);
    return response.data.data;
  }

  async createCelebrationVenueSearch(celebrationId: number, payload: CreateCelebrationVenueSearchDto): Promise<CelebrationVenueSearch> {
    const response = await apiClient.post(`/celebrations/${celebrationId}/venue-searches`, payload);
    return response.data.data;
  }

  async deleteCelebrationVenueSearch(celebrationId: number, searchId: number): Promise<void> {
    await apiClient.delete(`/celebrations/${celebrationId}/venue-searches/${searchId}`);
  }

  /**
   * Search Google Places directly
   */
  // Removed searchGooglePlaces: use searchVenues instead


  /**
   * Ensure venue exists (create if needed) - key method for event creation
   */
  async ensureVenue(placeId: string): Promise<{ venue_id: number; created: boolean }> {
    const response = await apiClient.post(`/venues/ensure/${placeId}`);
    return response.data.data;
  }

  async addVenueToCelebration(celebrationId: number, venueId: number): Promise<void> {
    await apiClient.post(`/celebrations/${celebrationId}/venues`, { venue_id: venueId });
  }

  async removeVenueFromCelebration(celebrationId: number, venueId: number): Promise<void> {
    await apiClient.delete(`/celebrations/${celebrationId}/venues/${venueId}`);
  }

  async updateVenueNotes(venueId: number, notes: string | null): Promise<Venue> {
    const response = await apiClient.put(`/venues/${venueId}/notes`, { notes });
    return response.data.data;
  }
}

export const venueService = new VenueService();
